import 'chartjs-adapter-luxon';

import { ProcessNames } from '@parallel-fluidics/shared/constants';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import ChartAnnotation from 'chartjs-plugin-annotation';
import * as d3 from 'd3';
import { formatVacuumFeedback, getVacuumFeedbackValues } from 'features/plc/components/common';
import {
  BONDING_CYCLE_STATE_LABELS,
  FORMING_CYCLE_STATE_LABELS,
  VACUUM_ZONE_LABELS,
} from 'features/plc/contracts/pressHMI.schema';

ChartJS.register(
  ChartAnnotation,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  TimeScale,
  Legend,
);

const DARK_MODE_BORDER_COLOR = '#232b37';
const defaultPalette = [
  'red',
  'blue',
  ...d3.schemeCategory10,
  ...d3.schemeSet3,
];

const TM3_TUNING_SERIES = [
  { key: 'upperToolTempAvg', label: 'Top Temp' },
  { key: 'lowerToolTempAvg', label: 'Bottom Temp' },
  { key: 'upperAppliedSetpoint', label: 'Top Setpoint' },
  { key: 'lowerAppliedSetpoint', label: 'Bottom Setpoint' },
  { key: 'upperCoolerOn', label: 'Upper Cooler' },
  { key: 'lowerCoolerOn', label: 'Lower Cooler' },
  { key: 'upperToolRtd1', label: 'Top RTD 1' },
  { key: 'upperToolRtd2', label: 'Top RTD 2' },
  { key: 'upperToolRtd3', label: 'Top RTD 3' },
  { key: 'lowerToolRtd1', label: 'Bottom RTD 1' },
  { key: 'lowerToolRtd2', label: 'Bottom RTD 2' },
  { key: 'lowerToolRtd3', label: 'Bottom RTD 3' },
  { key: 'upperPeekRtd', legacyKey: 'upperHeaterRtd', label: 'Upper PEEK RTD' },
  { key: 'lowerHeaterRtd', label: 'Lower Heater RTD' },
  { key: 'upperHeaterOutputPct', label: 'Upper Heater Output' },
  { key: 'lowerHeaterOutputPct', label: 'Lower Heater Output' },
];

const TM3_PROCESS_SERIES = [
  { key: 'upperToolTempAvg', label: 'Top Temp' },
  { key: 'lowerToolTempAvg', label: 'Bottom Temp' },
  { key: 'vacuumZoneOpen', label: 'Vacuum Zone' },
  { key: 'vacuum4to20mAFeedback', label: 'Vacuum Pressure' },
  { key: 'ionizerOn', label: 'Ionizer' },
  { key: 'cycleRunning', label: 'Cycle Running' },
  { key: 'cycleState', label: 'Cycle State' },
  { key: 'upperCoolerOn', label: 'Upper Cooler' },
  { key: 'lowerCoolerOn', label: 'Lower Cooler' },
  { key: 'upperToolRtd1', label: 'Top RTD 1' },
  { key: 'upperToolRtd2', label: 'Top RTD 2' },
  { key: 'upperToolRtd3', label: 'Top RTD 3' },
  { key: 'lowerToolRtd1', label: 'Bottom RTD 1' },
  { key: 'lowerToolRtd2', label: 'Bottom RTD 2' },
  { key: 'lowerToolRtd3', label: 'Bottom RTD 3' },
  { key: 'upperPeekRtd', legacyKey: 'upperHeaterRtd', label: 'Upper PEEK RTD' },
  { key: 'lowerHeaterRtd', label: 'Lower Heater RTD' },
  { key: 'upperHeaterOutputPct', label: 'Upper Heater Output' },
  { key: 'lowerHeaterOutputPct', label: 'Lower Heater Output' },
];

const TM2_PROCESS_SERIES = [
  { key: 'UpperHFTS_ToolTemp', label: 'Top Temp' },
  { key: 'LowerHFTS_ToolTemp', label: 'Bottom Temp' },
  { key: 'Vacuum', label: 'Vacuum' },
  { key: 'UpperHFTS_HP1Temp', label: 'Top HP1' },
  { key: 'UpperHFTS_HP2Temp', label: 'Top HP2' },
  { key: 'UpperHFTS_HP3Temp', label: 'Top HP3' },
  { key: 'UpperHFTS_HP4Temp', label: 'Top HP4' },
  { key: 'UpperHFTS_HP5Temp', label: 'Top HP5' },
  { key: 'UpperHFTS_HP6Temp', label: 'Top HP6' },
  { key: 'UpperHFTS_HotBlockTemp', label: 'Upper Hot Block' },
  { key: 'UpperHFTS_FrontBlockTemp', label: 'Upper Front Block' },
  { key: 'UpperHFTS_RearBlockTemp', label: 'Upper Rear Block' },
  { key: 'LowerHFTS_HP1Temp', label: 'Bottom HP1' },
  { key: 'LowerHFTS_HP2Temp', label: 'Bottom HP2' },
  { key: 'LowerHFTS_HP3Temp', label: 'Bottom HP3' },
  { key: 'LowerHFTS_HP4Temp', label: 'Bottom HP4' },
  { key: 'LowerHFTS_HP5Temp', label: 'Bottom HP5' },
  { key: 'LowerHFTS_HP6Temp', label: 'Bottom HP6' },
  { key: 'LowerHFTS_HotBlockTemp', label: 'Lower Hot Block' },
  { key: 'LowerHFTS_FrontBlockTemp', label: 'Lower Front Block' },
  { key: 'LowerHFTS_RearBlockTemp', label: 'Lower Rear Block' },
];

const TM3_BOOLEAN_LABELS = ['Upper Cooler', 'Lower Cooler', 'Ionizer', 'Cycle Running'];
const TM3_PERCENT_LABELS = ['Upper Heater Output', 'Lower Heater Output'];

const getTooltipLabel = ({ label, raw, processName }) => {
  if (raw[label] === null || raw[label] === undefined) {
    return `${label}: N/A`;
  }

  if (TM3_BOOLEAN_LABELS.includes(label)) {
    return `${label}: ${raw[label] ? 'On' : 'Off'}`;
  }
  if (label === 'Vacuum Zone') {
    const vacuumValue = raw[label] / 20;
    return `${label}: ${VACUUM_ZONE_LABELS[vacuumValue] || vacuumValue}`;
  }
  if (label === 'Vacuum Pressure') {
    return `${label}: ${formatVacuumFeedback(raw.vacuumPressureRaw)}`;
  }
  if (label === 'Cycle State') {
    const cycleLabel = processName === ProcessNames.FORMING
      ? FORMING_CYCLE_STATE_LABELS[raw[label]]
      : BONDING_CYCLE_STATE_LABELS[raw[label]];
    return `${label}: ${cycleLabel}`;
  }
  if (TM3_PERCENT_LABELS.includes(label)) {
    return `${label}: ${raw[label].toFixed(1)}%`;
  }

  return `${label}: ${raw[label].toFixed(1)}°C`;
};

const getTm3SeriesValue = (row, key, legacyKey = null) => {
  if (row[key] !== undefined && row[key] !== null) {
    return row[key];
  }

  return legacyKey && row[legacyKey] !== undefined && row[legacyKey] !== null
    ? row[legacyKey]
    : null;
};

const buildTm3DataPoint = ({
  row,
  key,
  legacyKey,
  label,
}) => {
  const value = getTm3SeriesValue(row, key, legacyKey);

  if (label === 'Vacuum Pressure') {
    return {
      timestamp: row.timestamp,
      [label]: getVacuumFeedbackValues(value)?.vacuumPercent ?? null,
      vacuumPressureRaw: value,
    };
  }

  return { timestamp: row.timestamp, [label]: value };
};

const createVerticalLinePlugin = () => ({
  id: 'verticalLine',
  defaults: {
    width: 1,
    dash: [3, 3],
  },
  // eslint-disable-next-line no-param-reassign
  afterInit: (chart) => { chart.verticalLine = { x: 0 }; },
  afterEvent: (chart, args) => {
    const { inChartArea, event } = args;
    const { x } = event;

    // eslint-disable-next-line no-param-reassign
    chart.verticalLine = { x, draw: inChartArea };
    chart.draw();
  },
  beforeDatasetsDraw: (chart, _, opts) => {
    const { ctx, chartArea, verticalLine } = chart;
    const { top, bottom } = chartArea;
    const { x, draw } = verticalLine || {};
    if (!draw) return;

    ctx.save();
    ctx.beginPath();
    ctx.lineWidth = opts.width;
    ctx.strokeStyle = opts.color;
    ctx.setLineDash(opts.dash);
    ctx.moveTo(x, bottom);
    ctx.lineTo(x, top);
    ctx.stroke();
    ctx.restore();
  },
});

const getCycleState = (row, processName) => {
  if (processName === ProcessNames.FORMING) {
    return row.formingCycleState;
  }
  if (processName === ProcessNames.BONDING) {
    return row.bondingCycleState;
  }
  return undefined;
};

const normalizeTm3PlcRow = (row, processName) => ({
  ...row,
  timestamp: new Date(row.time || row.timestamp),
  upperCoolerOn: row.upperCoolerOn ? 60 : 0,
  lowerCoolerOn: row.lowerCoolerOn ? 55 : 0,
  vacuumZoneOpen: row.vacuumPowerOn ? row.vacuumZoneOpen * 20 : 0,
  ionizerOn: row.ionizerOn ? 45 : 0,
  cycleRunning: row.cycleRunning ? 10 : 0,
  cycleState: getCycleState(row, processName),
});

const buildTm3Datasets = ({
  plcData,
  series,
  processName,
  defaultVisibleLabels,
  palette = defaultPalette,
}) => series
  .filter(({ key, legacyKey }) => plcData.some((row) => (
    getTm3SeriesValue(row, key, legacyKey) !== null
  )))
  .map(({
    key,
    legacyKey,
    label,
  }, index) => ({
    label,
    data: plcData.map((row) => buildTm3DataPoint({
      row,
      key,
      legacyKey,
      label,
    })),
    borderColor: palette[index],
    backgroundColor: palette[index],
    stepped: ['Vacuum Zone', ...TM3_BOOLEAN_LABELS, 'Cycle State'].includes(label),
    yAxisID: 'y',
    pointStyle: false,
    tension: 0.1,
    borderWidth: 2,
    hidden: !defaultVisibleLabels.includes(label),
    tooltip: {
      callbacks: {
        label(context) {
          return getTooltipLabel({
            label,
            raw: context.raw,
            processName,
          });
        },
      },
    },
    parsing: {
      xAxisKey: 'timestamp',
      yAxisKey: label,
    },
  }));

const buildTm2Datasets = ({
  plcData,
  defaultVisibleLabels,
  palette = defaultPalette,
}) => TM2_PROCESS_SERIES
  .filter(({ key }) => plcData.some((row) => row[key] !== undefined && row[key] !== null))
  .map(({ key, label }, index) => ({
    label,
    data: plcData.map((row) => ({ timestamp: row.timestamp, [label]: row[key] })),
    borderColor: palette[index],
    backgroundColor: palette[index],
    stepped: key === 'Vacuum',
    yAxisID: 'y',
    pointStyle: false,
    tension: 0.1,
    borderWidth: 2,
    hidden: !defaultVisibleLabels.includes(label),
    tooltip: {
      callbacks: {
        label(context) {
          if (key === 'Vacuum') {
            return `${label}: ${context.raw[label] ? 'On' : 'Off'}`;
          }
          return `${label}: ${context.raw[label].toFixed(1)}°C`;
        },
      },
    },
    parsing: {
      xAxisKey: 'timestamp',
      yAxisKey: label,
    },
  }));

const buildChartOptions = ({
  isDarkMode,
  isLoading,
  startTime = null,
}) => {
  const getSecondsLabel = (timestamp) => {
    if (!startTime) return null;
    const time = new Date(timestamp).getTime();
    const diffInSeconds = Math.round((time - startTime.getTime()) / 1000);
    const minutes = Math.floor(Math.abs(diffInSeconds) / 60);
    const seconds = Math.abs(diffInSeconds) % 60;
    const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    return diffInSeconds < 0 ? `-${formattedTime}` : formattedTime;
  };

  const options = {
    interaction: {
      mode: 'x',
      intersect: false,
    },
    stacked: false,
    animation: {
      duration: 0,
    },
    plugins: {
      verticalLine: {
        color: isDarkMode ? 'white' : 'black',
      },
      tooltip: {
        animation: false,
        // Only show the first instance of each dataset in the tooltip.
        // TODO: Figure out if there's a more efficient way to do this.
        // The current complexity is O(n^2) because filter is called for
        // each element in the array.
        filter: (element, index, array) => {
          const firstIndexWithLabel = {};
          for (let i = 0; i < array.length; i += 1) {
            if (firstIndexWithLabel[array[i].dataset.label] === undefined) {
              firstIndexWithLabel[array[i].dataset.label] = i;
            }
          }
          return firstIndexWithLabel[element.dataset.label] === index;
        },
        callbacks: {
          title: (tooltipItems) => {
            if (!tooltipItems.length) {
              return '';
            }
            const timestamp = tooltipItems[0].parsed.x;
            const secondsLabel = getSecondsLabel(timestamp);
            return secondsLabel
              ? `${secondsLabel} (${new Date(timestamp).toLocaleString()})`
              : new Date(timestamp).toLocaleString();
          },
        },
      },
      legend: {
        position: 'right',
      },
    },
    scales: {
      y: {
        title: { text: 'Temperature / Output', display: true },
        type: 'linear',
        display: true,
        position: 'left',
        ...(isDarkMode && {
          grid: {
            color: DARK_MODE_BORDER_COLOR,
          },
          border: {
            color: DARK_MODE_BORDER_COLOR,
          },
        }),
      },
      x: {
        title: { text: 'Time', display: true },
        type: 'time',
        time: {
          unit: 'minute',
          stepSize: 1,
          displayFormats: {
            minute: startTime ? 'mm:ss' : 'HH:mm:ss',
          },
        },
        ticks: {
          callback: (value) => {
            if (startTime) {
              return getSecondsLabel(value);
            }
            return new Date(value).toLocaleTimeString();
          },
        },
        ...(isDarkMode && {
          grid: {
            color: DARK_MODE_BORDER_COLOR,
          },
        }),
      },
    },
  };

  if (isLoading) {
    options.plugins.annotation = {
      annotations: {
        annotation: {
          type: 'box',
          backgroundColor: 'transparent',
          borderWidth: 0,
          label: {
            drawTime: 'afterDatasetsDraw',
            display: true,
            color: 'rgba(208, 208, 208, 0.5)',
            content: 'Loading...',
            font: {
              size: (ctx) => ctx.chart.chartArea.height / 4,
            },
            position: 'center',
          },
        },
      },
    };
  }

  return options;
};

export {
  TM3_TUNING_SERIES,
  TM3_PROCESS_SERIES,
  DARK_MODE_BORDER_COLOR,
  buildChartOptions,
  buildTm2Datasets,
  buildTm3Datasets,
  createVerticalLinePlugin,
  normalizeTm3PlcRow,
};
